/**
 * Modal: bonificar cupo WhatsApp a una clínica (Uso del producto).
 * Requiere cantidad + motivo; se audita en backend.
 */

import React, { useEffect, useId, useState } from 'react';
import { Button, Input } from '@/presentation/components/common';
import {
  createWhatsAppBonus,
  listWhatsAppBonuses,
  revokeWhatsAppBonus,
  type WhatsAppBonusDTO,
} from '@/infrastructure/api/whatsappBonusApi';

interface WhatsAppBonusModalProps {
  tenantId: string;
  clinicName: string;
  period: string;
  currentLimit: number;
  currentUsed: number;
  currentBonus: number;
  onClose: () => void;
  onChanged: () => void;
}

export function WhatsAppBonusModal({
  tenantId,
  clinicName,
  period,
  currentLimit,
  currentUsed,
  currentBonus,
  onClose,
  onChanged,
}: WhatsAppBonusModalProps) {
  const reasonId = useId();
  const scopeId = useId();
  const [messages, setMessages] = useState(50);
  const [reason, setReason] = useState('Trial comercial — envíos manuales');
  const [scope, setScope] = useState<'all' | 'manual_only'>('manual_only');
  const [history, setHistory] = useState<WhatsAppBonusDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const rows = await listWhatsAppBonuses(tenantId, {
        period,
        includeRevoked: true,
      });
      setHistory(rows);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    void loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, period]);

  const applyTrialPreset = () => {
    setMessages(50);
    setScope('manual_only');
    setReason('Trial comercial — envíos manuales');
  };

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!Number.isFinite(messages) || messages < 1) {
      setError('Indicá una cantidad de mensajes mayor a 0');
      return;
    }
    if (reason.trim().length < 5) {
      setError('El motivo es obligatorio (mín. 5 caracteres)');
      return;
    }
    setLoading(true);
    try {
      await createWhatsAppBonus(tenantId, {
        messages: Math.floor(messages),
        reason: reason.trim(),
        period,
        scope,
      });
      setReason('Trial comercial — envíos manuales');
      setMessages(50);
      setScope('manual_only');
      await loadHistory();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo registrar la bonificación');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: number) => {
    setError(null);
    if (revokeReason.trim().length < 3) {
      setError('Indicá el motivo de anulación (mín. 3 caracteres)');
      return;
    }
    setLoading(true);
    try {
      await revokeWhatsAppBonus(id, { revokeReason: revokeReason.trim() });
      setRevokeId(null);
      setRevokeReason('');
      await loadHistory();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo anular');
    } finally {
      setLoading(false);
    }
  };

  const previewLimit = currentLimit + Math.max(0, Math.floor(messages) || 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={onClose} />
        <div className="inline-block w-full max-w-lg transform overflow-hidden rounded-xl bg-white text-left align-bottom shadow-xl transition-all dark:bg-gray-800 sm:my-8 sm:align-middle">
          <div className="px-5 pt-5 pb-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bonificar WhatsApp
                </h3>
                <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">
                  {clinicName} · {period}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-xl leading-none text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <p className="mb-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-gray-900/50 dark:text-slate-300">
              Uso del mes: <strong className="tabular-nums">{currentUsed.toLocaleString()}</strong> /{' '}
              <strong className="tabular-nums">{currentLimit.toLocaleString()}</strong>
              {currentBonus > 0 ? (
                <>
                  {' '}
                  (incluye <strong className="tabular-nums">+{currentBonus}</strong> bonificados)
                </>
              ) : null}
              . Las bonificaciones valen solo este mes. Con alcance{' '}
              <strong>solo manual</strong>, no alimentan recordatorios automáticos.
            </p>

            <form onSubmit={handleGrant} className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={applyTrialPreset}>
                  Trial 50 (solo manual)
                </Button>
              </div>
              <Input
                label="Mensajes a bonificar"
                type="number"
                min={1}
                max={100000}
                value={messages}
                onChange={(e) => setMessages(parseInt(e.target.value, 10) || 0)}
                required
              />
              <div className="w-full">
                <label
                  htmlFor={scopeId}
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Alcance del cupo
                </label>
                <select
                  id={scopeId}
                  value={scope}
                  onChange={(e) =>
                    setScope(e.target.value === 'all' ? 'all' : 'manual_only')
                  }
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="manual_only">
                    Solo envíos manuales (no recordatorios automáticos)
                  </option>
                  <option value="all">Automáticos + manuales</option>
                </select>
              </div>
              <div className="w-full">
                <label
                  htmlFor={reasonId}
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Motivo (obligatorio)
                </label>
                <textarea
                  id={reasonId}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: trial comercial, cortesía por incidente, compensación…"
                  rows={3}
                  required
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              {messages > 0 ? (
                <p className="text-xs text-teal-700 dark:text-teal-300">
                  Cupo proyectado (según alcance): {currentUsed.toLocaleString()}/
                  {previewLimit.toLocaleString()}
                  {scope === 'manual_only'
                    ? ' · el auto sigue con el cupo del escalón Conexión'
                    : ''}
                </p>
              ) : null}
              {error ? (
                <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
              ) : null}
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>
                  Cerrar
                </Button>
                <Button type="submit" size="sm" disabled={loading}>
                  {loading ? 'Guardando…' : 'Bonificar'}
                </Button>
              </div>
            </form>

            <div className="mt-5 border-t border-slate-100 pt-4 dark:border-gray-700">
              <h4 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                Historial del mes
              </h4>
              {loadingHistory ? (
                <p className="text-xs text-gray-500">Cargando…</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-500">Sin bonificaciones en este período.</p>
              ) : (
                <ul className="max-h-48 space-y-2 overflow-y-auto">
                  {history.map((b) => (
                    <li
                      key={b.id}
                      className={`rounded-lg border px-2.5 py-2 text-xs ${
                        b.revokedAt
                          ? 'border-slate-100 bg-slate-50/80 opacity-70 dark:border-gray-700 dark:bg-gray-900/40'
                          : 'border-teal-100 bg-teal-50/40 dark:border-teal-900/40 dark:bg-teal-950/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold tabular-nums text-gray-900 dark:text-white">
                            +{b.messages.toLocaleString()} msgs
                            <span className="ml-1 font-normal text-slate-500">
                              · {b.scope === 'manual_only' ? 'solo manual' : 'auto+manual'}
                            </span>
                            {b.revokedAt ? (
                              <span className="ml-1 font-normal text-rose-600">· anulada</span>
                            ) : null}
                          </p>
                          <p className="mt-0.5 text-gray-600 dark:text-gray-300">{b.reason}</p>
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            {b.createdByName || '—'} ·{' '}
                            {new Date(b.createdAt).toLocaleString('es-AR')}
                          </p>
                          {b.revokedAt ? (
                            <p className="mt-0.5 text-[11px] text-rose-600/90">
                              Anuló {b.revokedByName || '—'}: {b.revokeReason}
                            </p>
                          ) : null}
                        </div>
                        {!b.revokedAt ? (
                          <button
                            type="button"
                            className="shrink-0 text-[11px] font-medium text-rose-600 hover:underline"
                            onClick={() => {
                              setRevokeId(b.id);
                              setRevokeReason('');
                            }}
                          >
                            Anular
                          </button>
                        ) : null}
                      </div>
                      {revokeId === b.id ? (
                        <div className="mt-2 space-y-1.5 border-t border-slate-200/80 pt-2 dark:border-gray-700">
                          <Input
                            label="Motivo de anulación"
                            value={revokeReason}
                            onChange={(e) => setRevokeReason(e.target.value)}
                            placeholder="Por qué se anula…"
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => setRevokeId(null)}
                            >
                              Cancelar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              disabled={loading}
                              onClick={() => void handleRevoke(b.id)}
                            >
                              Confirmar anulación
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
